import * as React from 'react';
import { styled } from '@mui/material/styles';
import Divider, { dividerClasses } from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { paperClasses } from '@mui/material/Paper';
import { listClasses } from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { useTranslation } from 'react-i18next';
import MenuButton from './MenuButton';
import * as api from '../../../modules/api';
import { forceLogout } from '../../utils/authSession';

const MenuItem = styled(MuiMenuItem)({
  margin: '2px 0',
});

export default function OptionsMenu() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [changePasswordLoading, setChangePasswordLoading] = React.useState(false);
  const [changePasswordError, setChangePasswordError] = React.useState('');
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleOpenSettings = () => {
    handleClose();
    setChangePasswordError('');
    setNewPassword('');
    setConfirmPassword('');
    setSettingsOpen(true);
  };
  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError('');
  };
  const handleLogout = () => {
    handleClose();
    forceLogout('user_menu');
  };
  const handleChangePassword = async () => {
    const trimmed = newPassword.trim();
    if (!trimmed) {
      setChangePasswordError(t('userMenu.errorEmpty'));
      return;
    }
    if (trimmed.length < 5) {
      setChangePasswordError(t('userMenu.errorTooShort'));
      return;
    }
    if (trimmed !== confirmPassword.trim()) {
      setChangePasswordError(t('userMenu.errorMismatch'));
      return;
    }
    setChangePasswordError('');
    setChangePasswordLoading(true);
    try {
      await api.changePassword(trimmed);
      handleCloseSettings();
      // Backend revokes old JWT via passwordChangedAt; clear local session too.
      forceLogout('password_changed');
    } catch (e) {
      setChangePasswordError(e?.response?.data?.message || e?.message || t('userMenu.errorFailed'));
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <>
      <MenuButton
        aria-label={t('userMenu.openMenu')}
        onClick={handleClick}
        sx={{ borderColor: 'transparent' }}
      >
        <MoreVertRoundedIcon />
      </MenuButton>
      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          [`& .${listClasses.root}`]: {
            padding: '4px',
          },
          [`& .${paperClasses.root}`]: {
            padding: 0,
          },
          [`& .${dividerClasses.root}`]: {
            margin: '4px -4px',
          },
        }}
      >
        <MenuItem onClick={handleOpenSettings}>
          <ListItemText>{t('userMenu.settings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            [`& .${listItemIconClasses.root}`]: {
              ml: 'auto',
              minWidth: 0,
            },
          }}
        >
          <ListItemText>{t('userMenu.logout')}</ListItemText>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      <Dialog open={settingsOpen} onClose={handleCloseSettings} maxWidth="xs" fullWidth>
        <DialogTitle>{t('userMenu.changePasswordTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <TextField
            fullWidth
            type="password"
            label={t('userMenu.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="new-password"
            error={Boolean(changePasswordError)}
            helperText={changePasswordError}
          />
          <TextField
            fullWidth
            type="password"
            label={t('userMenu.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            size="small"
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={changePasswordLoading}>
            {changePasswordLoading ? t('userMenu.saving') : t('userMenu.changePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
